import React, { useState, useEffect } from 'react';
import { Search, Filter, Activity, Grid3x3 as Grid3X3, List, Import as SortAsc, TrendingUp, Users, ExternalLink, RefreshCw, Plus } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../components/AuthProvider';
import { getProspectingDays, getProspectingDaysCount } from '../lib/api';
import { getAccessibleClientIds } from '../lib/multiClientUtils';
import { formatDate } from '../lib/utils';
import { usePagination } from '../hooks/usePagination';
import Pagination from '../components/ui/Pagination';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import Button from '../components/ui/Button';

const ProspectingPage: React.FC = () => {
  const { profile, isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const [prospectingDays, setProspectingDays] = useState([]);
  const [filteredProspectingDays, setFilteredProspectingDays] = useState([]);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [sortBy, setSortBy] = useState<'date' | 'calls' | 'rdvs' | 'conversion'>('date');
  const [showFilters, setShowFilters] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Pagination hook
  const pagination = usePagination({
    initialPageSize: 20,
    enableLogging: true,
  });

  const fetchProspectingDays = async (page: number = 1, pageSize: number = 20) => {
    if (!profile || !isAuthenticated) {
      console.log('Not authenticated, skipping fetch');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const clientIds = await getAccessibleClientIds(profile.user_id, profile.role);

      if (clientIds.length === 0 && profile.role !== 'super_admin' && profile.role !== 'admin') {
        console.log('No accessible clients found');
        setProspectingDays([]);
        setFilteredProspectingDays([]);
        pagination.setTotalItems(0);
        setIsLoading(false);
        return;
      }

      const offset = (page - 1) * pageSize;

      const [daysData, totalCount] = await Promise.all([
        getProspectingDays(clientIds, { limit: pageSize, offset }),
        getProspectingDaysCount(clientIds)
      ]);

      setProspectingDays(daysData);
      setFilteredProspectingDays(daysData);
      pagination.setTotalItems(totalCount);
    } catch (error) {
      console.error('Error fetching prospecting days:', error);
      setError('Erreur lors du chargement des sessions de prospection');
    } finally {
      setIsLoading(false);
    }
  };

  // Update search term from URL params
  useEffect(() => {
    const searchParam = searchParams.get('search');
    if (searchParam) {
      setSearchTerm(searchParam);
    }
  }, [searchParams]);

  // Initial fetch when authenticated
  useEffect(() => {
    if (profile && isAuthenticated) {
      fetchProspectingDays(1, pagination.pageSize);
    }
  }, [profile, isAuthenticated]);

  // Handle page changes
  useEffect(() => {
    if (profile && isAuthenticated && pagination.currentPage > 1) {
      fetchProspectingDays(pagination.currentPage, pagination.pageSize);
    }
  }, [pagination.currentPage, pagination.pageSize, profile, isAuthenticated]);

  const handleRefresh = async () => {
    if (!profile || !isAuthenticated || isRefreshing) {
      return;
    }

    setIsRefreshing(true);
    setError(null);

    try {
      // Reset to first page and fetch fresh data
      pagination.setPage(1);
      await fetchProspectingDays(1, pagination.pageSize);
    } catch (error) {
      console.error('Error refreshing prospecting days:', error);
      setError('Erreur lors de l\'actualisation des sessions de prospection');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Filter and sort prospecting days
  useEffect(() => {
    let filtered = [...prospectingDays];

    // Apply search filter
    if (searchTerm.trim()) {
      filtered = filtered.filter(day =>
        (day.clients?.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (day.sdr_first_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (day.sdr_last_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        formatDate(day.date).toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply sorting
    filtered = filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date':
          return new Date(b.date).getTime() - new Date(a.date).getTime();
        case 'calls':
          return (b.calls_count || 0) - (a.calls_count || 0);
        case 'rdvs':
          return (b.rdvs_booked || 0) - (a.rdvs_booked || 0);
        case 'conversion':
          const aRate = a.calls_count > 0 ? (a.rdvs_booked || 0) / a.calls_count : 0;
          const bRate = b.calls_count > 0 ? (b.rdvs_booked || 0) / b.calls_count : 0;
          return bRate - aRate;
        default:
          return 0;
      }
    });

    setFilteredProspectingDays(filtered);
    
    // Reset to first page when filters change
    pagination.setPage(1);
  }, [searchTerm, prospectingDays, sortBy, pagination.setPage]);

  const sortOptions = [
    { value: 'date', label: 'Plus récents', icon: SortAsc },
    { value: 'calls', label: 'Nb d\'appels', icon: SortAsc },
    { value: 'rdvs', label: 'RDVs bookés', icon: SortAsc },
    { value: 'conversion', label: 'Taux conversion', icon: SortAsc },
  ];

  const getConversionRate = (day: any) => {
    if (!day.calls_count || day.calls_count === 0) return 0;
    return ((day.rdvs_booked || 0) / day.calls_count * 100);
  };

  if (pagination.isLoading && prospectingDays.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-green-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex justify-center items-center h-64">
            <LoadingSpinner size="lg" text="Chargement des sessions de prospection..." />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-green-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Section */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            {/* Title and Description */}
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-gray-900 to-green-600 bg-clip-text text-transparent">
                Prospection
              </h1>
              <p className="text-gray-600 mt-2 text-lg">
                Suivez toutes vos activités de prospection
              </p>
              {pagination.error && (
                <div className="mt-3 bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg">
                  {error}
                </div>
              )}
              {!isLoading && (
                <div className="flex items-center mt-3 text-sm text-gray-500">
                  <Activity className="h-4 w-4 mr-2" />
                  <span>{pagination.totalItems} journée{pagination.totalItems !== 1 ? 's' : ''} de prospection</span>
                </div>
              )}
            </div>

            {/* Search and Controls */}
            <div className="flex flex-col sm:flex-row gap-4 lg:min-w-96">
              {/* Add Prospecting Day Button - For super_admin and admin */}
              {(profile?.role === 'super_admin' || profile?.role === 'admin') && (
                <Button
                  onClick={() => window.open('https://feedback-sdr.hook-agency.app/', '_blank')}
                  icon={<Plus className="h-5 w-5" />}
                  className="bg-green-600 hover:bg-green-700 focus:ring-green-500 whitespace-nowrap"
                >
                  Ajouter une journée
                </Button>
              )}
              
              {/* Search Bar */}
              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder="Rechercher une session..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full px-4 py-3 border border-gray-200 rounded-xl bg-white/80 backdrop-blur-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 shadow-sm hover:shadow-md"
                />
              </div>

              {/* Filter Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`px-4 py-3 rounded-xl border transition-all duration-200 flex items-center space-x-2 ${
                  showFilters 
                    ? 'bg-green-500 text-white border-green-500 shadow-lg' 
                    : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 shadow-sm hover:shadow-md'
                }`}
              >
                <Filter className="h-5 w-5" />
                <span className="hidden sm:inline">Filtres</span>
              </button>

              {/* Refresh Button */}
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="px-4 py-3 rounded-xl border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 shadow-sm hover:shadow-md transition-all duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className={`h-5 w-5 ${isRefreshing ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">{isRefreshing ? 'Actualisation...' : 'Actualiser'}</span>
              </button>
            </div>
          </div>

          {/* Filters Panel */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-6 bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden"
              >
                <div className="p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    {/* Sort Options */}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                      <span className="text-sm font-medium text-gray-700">Trier par :</span>
                      <div className="flex flex-wrap gap-2">
                        {sortOptions.map((option) => (
                          <button
                            key={option.value}
                            onClick={() => setSortBy(option.value as any)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center space-x-2 ${
                              sortBy === option.value
                                ? 'bg-green-100 text-green-700 border border-green-200'
                                : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
                            }`}
                          >
                            <option.icon className="h-4 w-4" />
                            <span>{option.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Search Results Info */}
          {searchTerm && (
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4"
            >
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <Search className="h-4 w-4 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-green-900">
                    {filteredProspectingDays.length} résultat{filteredProspectingDays.length !== 1 ? 's' : ''} trouvé{filteredProspectingDays.length !== 1 ? 's' : ''}
                  </p>
                  <p className="text-xs text-green-700">
                    pour la recherche "{searchTerm}"
                  </p>
                </div>
                <button
                  onClick={() => setSearchTerm('')}
                  className="ml-auto text-green-600 hover:text-green-800 text-sm font-medium"
                >
                  Effacer
                </button>
              </div>
            </motion.div>
          )}
        </motion.div>

        {/* Stats Section - Moved to top */}
        {!isLoading && filteredProspectingDays.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-8 bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-sm"
          >
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 text-center">
              <div>
                <div className="text-xl sm:text-2xl font-bold text-gray-900">{pagination.totalItems}</div>
                <div className="text-sm text-gray-500">Total sessions</div>
              </div>
              <div>
                <div className="text-xl sm:text-2xl font-bold text-green-600">
                  {filteredProspectingDays.reduce((sum, day) => sum + (day.calls_count || 0), 0)}
                </div>
                <div className="text-sm text-gray-500">Total appels</div>
              </div>
              <div>
                <div className="text-xl sm:text-2xl font-bold text-blue-600">
                  {filteredProspectingDays.reduce((sum, day) => sum + (day.rdvs_booked || 0), 0)}
                </div>
                <div className="text-sm text-gray-500">RDVs bookés</div>
              </div>
              <div>
                <div className="text-xl sm:text-2xl font-bold text-purple-600">
                  {(() => {
                    const totalCalls = filteredProspectingDays.reduce((sum, day) => sum + (day.calls_count || 0), 0);
                    const totalRdvs = filteredProspectingDays.reduce((sum, day) => sum + (day.rdvs_booked || 0), 0);
                    return totalCalls > 0 ? ((totalRdvs / totalCalls) * 100).toFixed(1) : '0.0';
                  })()}%
                </div>
                <div className="text-sm text-gray-500">Taux global</div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Prospecting Days Grid */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          {/* Loading overlay for pagination */}
          {isLoading && prospectingDays.length > 0 && (
            <div className="mb-6 flex justify-center">
              <LoadingSpinner text="Chargement des sessions..." />
            </div>
          )}

          {filteredProspectingDays.length === 0 ? (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-16"
            >
              <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center">
                <Activity className="h-12 w-12 text-green-500" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {isLoading ? 'Chargement...' : 'Aucune session trouvée'}
              </h3>
              <p className="text-gray-500 max-w-md mx-auto">
                {searchTerm 
                  ? 'Aucune session de prospection ne correspond à vos critères de recherche.'
                  : isLoading ? 'Veuillez patienter...' : 'Aucune session de prospection enregistrée pour le moment.'
                }
              </p>
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProspectingDays.map((day, index) => (
                <motion.div
                  key={day.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -4 }}
                  className="group"
                >
                  <Link
                    to={`/prospecting/${day.id}`}
                    className="block bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-xl hover:border-green-200 transition-all duration-300"
                  >
                    {/* Header avec date */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center">
                          <Activity className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 group-hover:text-green-600 transition-colors">
                            {formatDate(day.date)}
                          </h3>
                          {day.clients && (
                            <p className="text-sm text-gray-500">{day.clients.name}</p>
                          )}
                        </div>
                      </div>
                      {day.rdvs_booked > 0 && (
                        <div className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-medium">
                          {day.rdvs_booked} RDV
                        </div>
                      )}
                    </div>

                    {/* SDR Info */}
                    {(day.sdr_first_name || day.sdr_last_name) && (
                      <div className="flex items-center text-sm text-gray-600 mb-4">
                        <Users className="h-4 w-4 mr-2 text-gray-400" />
                        <span>{day.sdr_first_name} {day.sdr_last_name}</span>
                      </div>
                    )}

                    {/* Metrics */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="text-center">
                        <div className="text-lg font-bold text-gray-900">{day.calls_count || 0}</div>
                        <div className="text-xs text-gray-500">Appels</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-green-600">{day.rdvs_booked || 0}</div>
                        <div className="text-xs text-gray-500">RDVs</div>
                      </div>
                    </div>

                    {/* Conversion Rate */}
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm text-gray-600">Taux de conversion</span>
                      <div className="flex items-center space-x-2">
                        <TrendingUp className="h-4 w-4 text-green-500" />
                        <span className="font-medium text-green-600">
                          {getConversionRate(day).toFixed(1)}%
                        </span>
                      </div>
                    </div>

                    {/* Footer avec indicateur d'action */}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <span className="text-xs text-gray-500 font-medium">Voir les détails</span>
                      <ExternalLink className="h-4 w-4 text-gray-400 group-hover:text-green-500 group-hover:translate-x-1 transition-all duration-200" />
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Pagination */}
        {!isLoading && pagination.totalPages > 1 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-8"
          >
            <Pagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              onPageChange={pagination.setPage}
              isLoading={isLoading}
              pageSize={pagination.pageSize}
              onPageSizeChange={pagination.setPageSize}
              totalItems={pagination.totalItems}
            />
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default ProspectingPage;